import React, { useState, useEffect } from 'react';
import { Table, Select, Card, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Key } from 'react';
import apiService from '@/services/api.service';
import type { ApiResponse } from '@/types/api';
import { logger } from '@/utils/logger';
import { formatDate } from '@/utils/date';
import { API_ENDPOINTS } from '@/constants/api.constants';

interface SubscriptionPlan {
    id: number;
    name: string;
    description: string | null;
    price: number;
    durationDays: number;
    features: string[] | null;
    isActive: boolean;
    sortOrder: number;
}

const SubscriptionsPage: React.FC = () => {
    const [subscriptions, setSubscriptions] = useState<any[]>([]);
    const [filteredSubscriptions, setFilteredSubscriptions] = useState<any[]>([]);
    const [isSubscriptionsLoading, setIsSubscriptionsLoading] = useState(false);
    const [subscriptionStatusFilter, setSubscriptionStatusFilter] = useState<string>('all');
    const [subscriptionPlanFilter, setSubscriptionPlanFilter] = useState<string>('all');
    const [subscriptionStatistics, setSubscriptionStatistics] = useState<any>(null);
    const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);

    const fetchSubscriptions = async () => {
        try {
            setIsSubscriptionsLoading(true);
            const response = await apiService.get<ApiResponse<any[]>>(
                API_ENDPOINTS.SUBSCRIPTIONS.BASE
            );
            const subscriptionsData = Array.isArray(response.data.data) ? response.data.data : [];
            
            // Нормализуем данные, чтобы избежать ошибок рендеринга
            const normalizedSubscriptions = subscriptionsData.map((sub: any) => ({
                ...sub,
                id: sub.id || Math.random(),
                status: sub.status || 'pending',
                price: sub.price !== null && sub.price !== undefined ? Number(sub.price) : 0,
                startDate: sub.startDate || null,
                endDate: sub.endDate || null,
                club: sub.club || null,
                subscriptionPlan: sub.subscriptionPlan || null,
            }));

            setSubscriptions(normalizedSubscriptions);
            filterSubscriptions();
        } catch (error) {
            logger.error('Error fetching subscriptions:', error);
            message.error('Ошибка при загрузке списка подписок');
            setSubscriptions([]);
        } finally {
            setIsSubscriptionsLoading(false);
        }
    };

    const fetchSubscriptionStatistics = async () => {
        try {
            const response = await apiService.get<ApiResponse<any>>(
                API_ENDPOINTS.SUBSCRIPTIONS.STATISTICS
            );
            setSubscriptionStatistics(response.data.data);
        } catch (error) {
            logger.error('Error fetching subscription statistics:', error);
        }
    };

    const fetchSubscriptionPlans = async () => {
        try {
            const response = await apiService.get<ApiResponse<SubscriptionPlan[]>>(
                API_ENDPOINTS.SUBSCRIPTION_PLANS.BASE
            );
            setSubscriptionPlans(Array.isArray(response.data.data) ? response.data.data : []);
        } catch (error) {
            logger.error('Error fetching subscription plans:', error);
        }
    };

    const filterSubscriptions = () => {
        let filtered = subscriptions.filter(sub => sub != null);

        if (subscriptionStatusFilter !== 'all') {
            filtered = filtered.filter(sub => sub && sub.status === subscriptionStatusFilter);
        }

        if (subscriptionPlanFilter !== 'all') {
            filtered = filtered.filter(sub => {
                if (!sub || !sub.subscriptionPlan) return false;
                const planId = sub.subscriptionPlan.id;
                if (planId === null || planId === undefined) return false;
                return Number(planId) === parseInt(subscriptionPlanFilter);
            });
        }

        setFilteredSubscriptions(filtered);
    };

    useEffect(() => {
        fetchSubscriptions();
        fetchSubscriptionStatistics();
        fetchSubscriptionPlans();
    }, []);

    useEffect(() => {
        filterSubscriptions();
    }, [subscriptions, subscriptionStatusFilter, subscriptionPlanFilter]);

    const subscriptionColumns: ColumnsType<any> = [
        {
            title: 'Клуб',
            key: 'club',
            render: (_: unknown, record: any) => {
                if (!record || !record.club) return 'Не указан';
                const clubName = record.club.name || '';
                const clubCity = record.club.city || '';
                return clubName || clubCity ? `${clubName}${clubCity ? ` (${clubCity})` : ''}` : 'Не указан';
            },
            sorter: (a: any, b: any) => {
                const aName = (a && a.club && a.club.name) ? String(a.club.name) : '';
                const bName = (b && b.club && b.club.name) ? String(b.club.name) : '';
                return aName.localeCompare(bName);
            },
        },
        {
            title: 'План подписки',
            key: 'subscriptionPlan',
            render: (_: unknown, record: any) => {
                if (!record) return 'Не указан';
                return (record.subscriptionPlan && record.subscriptionPlan.name) 
                    ? String(record.subscriptionPlan.name) 
                    : 'Не указан';
            },
            filters: [
                { text: 'Все', value: 'all' },
            ],
            onFilter: (value: boolean | Key, record: any) => {
                if (!record) return false;
                if (value === 'all') return true;
                return record.subscriptionPlan && record.subscriptionPlan.id === parseInt(value.toString());
            },
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            key: 'status',
            render: (status: string | null | undefined) => {
                if (!status) return <Tag color="default">Не указан</Tag>;
                const statusConfig: Record<string, { color: string; text: string }> = {
                    active: { color: 'green', text: 'Активна' },
                    cancelled: { color: 'red', text: 'Отменена' },
                    expired: { color: 'orange', text: 'Истекла' },
                    pending: { color: 'blue', text: 'Ожидает' }
                };
                const config = statusConfig[String(status)] || { color: 'default', text: String(status) };
                return <Tag color={config.color}>{config.text}</Tag>;
            },
            filters: [
                { text: 'Активна', value: 'active' },
                { text: 'Отменена', value: 'cancelled' },
                { text: 'Истекла', value: 'expired' },
                { text: 'Ожидает', value: 'pending' },
            ],
            onFilter: (value: boolean | Key, record: any) => {
                if (!record) return false;
                return String(record.status || '') === value.toString();
            },
        },
        {
            title: 'Дата начала',
            dataIndex: 'startDate',
            key: 'startDate',
            render: (date: string | null | undefined) => {
                if (!date) return '-';
                try {
                    return formatDate(date);
                } catch (e) {
                    return String(date);
                }
            },
            sorter: (a: any, b: any) => {
                const aDate = a && a.startDate ? new Date(a.startDate).getTime() : 0;
                const bDate = b && b.startDate ? new Date(b.startDate).getTime() : 0;
                return aDate - bDate;
            },
        },
        {
            title: 'Дата окончания',
            dataIndex: 'endDate',
            key: 'endDate',
            render: (date: string | null | undefined) => {
                if (!date) return '-';
                try {
                    return formatDate(date);
                } catch (e) {
                    return String(date);
                }
            },
            sorter: (a: any, b: any) => {
                if (!a || !a.endDate) return 1;
                if (!b || !b.endDate) return -1;
                try {
                    return new Date(a.endDate).getTime() - new Date(b.endDate).getTime();
                } catch (e) {
                    return 0;
                }
            },
        },
        {
            title: 'Цена',
            dataIndex: 'price',
            key: 'price',
            render: (price: number | null | undefined) => {
                if (price === null || price === undefined) return '0.00 ₽';
                try {
                    const priceNum = typeof price === 'number' ? price : parseFloat(String(price));
                    return `${isNaN(priceNum) ? 0 : priceNum.toFixed(2)} ₽`;
                } catch (e) {
                    return '0.00 ₽';
                }
            },
            sorter: (a: any, b: any) => {
                const aPrice = (a && typeof a.price === 'number') ? a.price : (a && a.price ? parseFloat(String(a.price)) : 0);
                const bPrice = (b && typeof b.price === 'number') ? b.price : (b && b.price ? parseFloat(String(b.price)) : 0);
                return (isNaN(aPrice) ? 0 : aPrice) - (isNaN(bPrice) ? 0 : bPrice);
            },
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <div>
                        <span style={{ marginRight: 8 }}>Статус:</span>
                        <Select
                            value={subscriptionStatusFilter}
                            style={{ width: 150 }}
                            onChange={setSubscriptionStatusFilter}
                            options={[
                                { value: 'all', label: 'Все' },
                                { value: 'active', label: 'Активные' },
                                { value: 'cancelled', label: 'Отмененные' },
                                { value: 'expired', label: 'Истекшие' },
                                { value: 'pending', label: 'Ожидающие' }
                            ]}
                        />
                    </div>
                    <div>
                        <span style={{ marginRight: 8 }}>План:</span>
                        <Select
                            value={subscriptionPlanFilter}
                            style={{ width: 200 }}
                            onChange={setSubscriptionPlanFilter}
                            options={[
                                { value: 'all', label: 'Все' },
                                ...subscriptionPlans.map(plan => ({
                                    value: plan.id.toString(),
                                    label: plan.name
                                }))
                            ]}
                        />
                    </div>
                </div>
            </div>
            {subscriptionStatistics && (
                <div style={{ marginBottom: 16, display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <Card size="small" style={{ flex: 1, minWidth: 150 }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                                {subscriptionStatistics.total}
                            </div>
                            <div style={{ color: '#666' }}>Всего подписок</div>
                        </div>
                    </Card>
                    <Card size="small" style={{ flex: 1, minWidth: 150 }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#52c41a' }}>
                                {subscriptionStatistics.active}
                            </div>
                            <div style={{ color: '#666' }}>Активных</div>
                        </div>
                    </Card>
                    <Card size="small" style={{ flex: 1, minWidth: 150 }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#faad14' }}>
                                {subscriptionStatistics.totalRevenue?.toFixed(2) || '0.00'} ₽
                            </div>
                            <div style={{ color: '#666' }}>Доход</div>
                        </div>
                    </Card>
                    <Card size="small" style={{ flex: 1, minWidth: 150 }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#722ed1' }}>
                                {subscriptionStatistics.clubsWithExtendedLicense ?? 0}
                            </div>
                            <div style={{ color: '#666' }}>Клубов с расширенной лицензией</div>
                        </div>
                    </Card>
                </div>
            )}
            <Table
                columns={subscriptionColumns}
                dataSource={filteredSubscriptions}
                rowKey={(record) => record?.id?.toString() || String(Math.random())}
                loading={isSubscriptionsLoading}
                pagination={{
                    showSizeChanger: true,
                    showTotal: (total) => `Всего ${total} подписок`,
                }}
            />
        </div>
    );
};

export default SubscriptionsPage;
